/* Epic — the behaviour shared by every request form (test yardage, quote,
   contact). Progressive: without this file each form is a plain Shopify
   contact form that posts and works; with it the form counts what is left,
   ticks finished steps, says what is wrong next to the field instead of in a
   browser bubble, and cannot be sent twice.

   Markup contract (see the three sections):
     form.epic-fm           the form, carries .epic-fm
     .epic-fm__f                    one field wrapper (input/textarea inside)
     [data-fm-need-one]             a group where at least one box must be ticked
     .epic-fm__step                 a step; ticked when its required parts pass,
                                    or, if it has none, when anything in it is filled
     [data-fm-done] [data-fm-total] the counter in the head; [data-fm-bar] the bar
     [data-fm-sum]                  optional summary line, built from data-fm-say
                                    on chosen chips/cards  */
(() => {
  const filled = (el) => {
    if (el.type === 'checkbox' || el.type === 'radio') return el.checked;
    return el.value.trim() !== '';
  };

  function wire(form) {
    if (form.dataset.fmWired) return;
    form.dataset.fmWired = '1';
    form.noValidate = true; // we show our own messages; the server still validates

    const fields = [...form.querySelectorAll('.epic-fm__f:not([data-fm-need-one])')];
    const groups = [...form.querySelectorAll('[data-fm-need-one]')];
    const steps = [...form.querySelectorAll('.epic-fm__step')];
    const done = form.querySelector('[data-fm-done]');
    const total = form.querySelector('[data-fm-total]');
    const sum = form.querySelector('[data-fm-sum]');
    const go = form.querySelector('.epic-fm__go');

    const ctlOf = (f) => f.querySelector('input:not([type=hidden]), textarea, select');
    const reqFields = fields.filter((f) => { const c = ctlOf(f); return c && c.required; });

    const fieldOk = (f) => { const c = ctlOf(f); return !c || (c.checkValidity() && (!c.required || filled(c))); };
    const groupOk = (g) => [...g.querySelectorAll('input')].some((i) => i.checked);

    function mark(f) {
      const c = ctlOf(f);
      if (!c) return;
      f.toggleAttribute('data-filled', filled(c) && c.checkValidity());
      if (f.hasAttribute('data-touched')) {
        const bad = !fieldOk(f);
        f.toggleAttribute('data-bad', bad);
        if (bad) c.setAttribute('aria-invalid', 'true'); else c.removeAttribute('aria-invalid');
      }
    }
    function markGroup(g) {
      if (g.hasAttribute('data-touched')) g.toggleAttribute('data-bad', !groupOk(g));
    }

    function count() {
      const parts = reqFields.length + groups.length;
      const ok = reqFields.filter(fieldOk).length + groups.filter(groupOk).length;
      if (done) done.textContent = ok;
      if (total) total.textContent = parts;
      form.style.setProperty('--fm-p', parts ? `${Math.round((ok / parts) * 100)}%` : '0%');
      form.toggleAttribute('data-complete', parts > 0 && ok === parts);

      steps.forEach((s) => {
        const sReq = reqFields.filter((f) => s.contains(f));
        const sGrp = groups.filter((g) => s.contains(g));
        let ok2;
        if (sReq.length + sGrp.length) ok2 = sReq.every(fieldOk) && sGrp.every(groupOk);
        else ok2 = [...s.querySelectorAll('input:not([type=hidden]), textarea, select')].some(filled);
        s.toggleAttribute('data-done', ok2);
      });

      if (sum) {
        const said = [...form.querySelectorAll('[data-fm-say]')]
          .filter((i) => i.checked)
          .reduce((acc, i) => { (acc[i.dataset.fmSay] ||= []).push(i.dataset.fmLabel || i.value); return acc; }, {});
        const keys = Object.keys(said);
        sum.toggleAttribute('data-on', keys.length > 0);
        sum.innerHTML = keys.length
          ? `${sum.dataset.fmLead || 'Your request'}: ` + keys.map((k) => `<b>${said[k].map(esc).join(', ')}</b>`).join(' · ')
          : '';
      }
    }
    const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

    fields.forEach((f) => {
      const c = ctlOf(f);
      if (!c) return;
      c.addEventListener('input', () => { mark(f); count(); });
      c.addEventListener('change', () => { mark(f); count(); });
      c.addEventListener('blur', () => { if (filled(c) || c.required) { f.setAttribute('data-touched', ''); mark(f); } });
      mark(f);
    });
    groups.forEach((g) => g.addEventListener('change', () => { g.setAttribute('data-touched', ''); markGroup(g); count(); }));
    form.addEventListener('change', count);

    form.addEventListener('submit', (e) => {
      fields.forEach((f) => { f.setAttribute('data-touched', ''); mark(f); });
      groups.forEach((g) => { g.setAttribute('data-touched', ''); markGroup(g); });
      const firstBad = form.querySelector('[data-bad]');
      if (firstBad) {
        e.preventDefault();
        const target = firstBad.querySelector('input:not([type=hidden]), textarea, select');
        firstBad.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (target) setTimeout(() => target.focus({ preventScroll: true }), 250);
        return;
      }
      if (go) go.setAttribute('aria-busy', 'true');
    });
    // A back-navigation can restore the page with the button still busy.
    window.addEventListener('pageshow', () => go && go.removeAttribute('aria-busy'));

    count();
    form.setAttribute('data-ready', '');
  }

  const init = () => document.querySelectorAll('form.epic-fm').forEach(wire);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
